import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";
import { LOGO_URL, CONTACT } from "@/data/site";

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const cols = [
  { title: "Hotel", links: [["About", "/about"], ["Our Story", "/our-story"], ["Vision & Mission", "/vision-mission"], ["Meet Our Team", "/team"], ["Testimonials", "/testimonials"], ["FAQ", "/faq"]] },
  { title: "Stay & Dine", links: [["Accommodation", "/rooms"], ["Room Amenities", "/amenities"], ["Restaurant", "/restaurant"], ["Baobab Bar", "/bar-lounge"], ["Special Offers", "/offers"], ["Gallery", "/gallery"]] },
  { title: "Facilities", links: [["Swimming Pool", "/facilities/swimming-pool"], ["Baobab Bar", "/bar-lounge"], ["Airport Transfers", "/facilities/airport-transfers"], ["Laundry Services", "/facilities/laundry-services"], ["Spa & Wellness", "/facilities/spa-wellness"]] },
];

export const Footer = () => (
  <footer className="border-t hairline bg-[#0d0d0d]" data-testid="main-footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
      <div className="lg:col-span-2">
        <div className="flex items-center gap-3 mb-6">
          <img src={LOGO_URL} alt="Zuri Sana Hotel" className="h-14 w-14 rounded-full object-cover ring-1 ring-[#d4af37]/50" />
          <div>
            <p className="font-serif-display text-2xl tracking-[0.15em] text-white">ZURI SANA</p>
            <p className="text-[10px] tracking-[0.4em] text-[#d4af37] uppercase">Hotel · Tanzania</p>
          </div>
        </div>
        <p className="text-[#a3a3a3] text-sm leading-relaxed max-w-sm mb-8">
          African luxury, world-class hospitality — a haven of golden calm on the island of Zanzibar, part of the Zuri Sana Hospitality Group.
        </p>
        <div className="space-y-3 text-sm text-white/70">
          <p className="flex items-center gap-3"><MapPin size={15} className="text-[#d4af37]" /> {CONTACT.address}</p>
          <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 hover:text-[#d4af37] transition-colors duration-300" data-testid="footer-phone-link"><Phone size={15} className="text-[#d4af37]" /> {CONTACT.phone}</a>
          <a href={`mailto:${CONTACT.emails.info}`} className="flex items-center gap-3 hover:text-[#d4af37] transition-colors duration-300" data-testid="footer-email-link"><Mail size={15} className="text-[#d4af37]" /> {CONTACT.emails.info}</a>
        </div>
        <div className="flex items-center gap-3 mt-8">
          <a href={CONTACT.socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="h-10 w-10 border hairline flex items-center justify-center text-white/60 hover:text-[#d4af37] hover:border-[#d4af37]/50 transition-colors duration-300" data-testid="footer-facebook-link"><Facebook size={16} /></a>
          <a href={CONTACT.socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="h-10 w-10 border hairline flex items-center justify-center text-white/60 hover:text-[#d4af37] hover:border-[#d4af37]/50 transition-colors duration-300" data-testid="footer-instagram-link"><Instagram size={16} /></a>
          <a href={CONTACT.socials.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="h-10 w-10 border hairline flex items-center justify-center text-white/60 hover:text-[#d4af37] hover:border-[#d4af37]/50 transition-colors duration-300" data-testid="footer-tiktok-link"><TikTokIcon size={15} /></a>
        </div>
      </div>
      {cols.map((col) => (
        <div key={col.title}>
          <p className="text-[11px] tracking-[0.3em] uppercase text-[#d4af37] mb-6">{col.title}</p>
          <ul className="space-y-3">
            {col.links.map(([label, to]) => (
              <li key={to}>
                <Link to={to} className="text-sm text-white/60 hover:text-white transition-colors duration-300">{label}</Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
    <div className="border-t hairline">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40">
        <p>© {new Date().getFullYear()} Zuri Sana Hotel · zurisanahotel.com</p>
        <div className="flex items-center gap-6">
          <Link to="/privacy-terms" className="hover:text-[#d4af37] transition-colors duration-300" data-testid="footer-privacy-link">Privacy & Terms</Link>
          <a href="https://codextz.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#d4af37] transition-colors duration-300" data-testid="footer-codex-link">Crafted by CodeX</a>
        </div>
      </div>
    </div>
  </footer>
);
